import React, { useContext } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import CustomInputNumber from "common/Inputs/CustomInputNumber.tsx";
import Text from "common/Typography/Text.tsx";
import {
  AmountWrapper,
  InputWrapper,
  StyledSelect,
} from "features/bots/BotForm/General/AmountSelector";
import { FormFieldTitle } from "features/tradingTerminal/common/styles.ts";
import { PERCENTAGE_AMOUNT_OPTIONS } from "@shared/features/bots/BotForm/common/constants.ts";
import { typography } from "@shared/common/Themes/typography.ts";
import { OverrideType } from "@shared/features/bots/BotForm/common/types.ts";
import OverrideTypeToggle from "features/bots/BotForm/common/OverrideTypeToggle.tsx";
import { AmountType, LanguageNameSpaces } from "@shared/types";
import ExpandCollapse from "common/Animations/ExpandCollapse.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface SignalAmountSelectorProps {
  amountValidationMessage: string;
  invalidTradeAmount?: boolean;
}

const SignalAmountSelector = ({
  amountValidationMessage,
  invalidTradeAmount,
}: SignalAmountSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { t: tBots } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { amount, setAmount, amountType, setAmountType } = useContext(
    TradingTerminalContext
  )!;

  const amountValueInput = (
    <InputWrapper>
      <CustomInputNumber
        value={amount}
        suffix="%"
        precision={2}
        onChange={setAmount}
        placeholder="0"
        bordered={false}
        useDebounce={true}
        stringMode={true}
      />
      <Text type="secondary">%</Text>
    </InputWrapper>
  );

  const amountTypeSelect = (
    <StyledSelect
      value={amountType}
      onChange={(value) => setAmountType(value as AmountType)}
      bordered={false}
      options={PERCENTAGE_AMOUNT_OPTIONS.map((option) => ({
        value: option.value,
        label: tBots(option.name),
      }))}
      mobileTitle={t("labels.amount_type")}
      showSearch={false}
      $isSplitInput={true}
      popupMatchSelectWidth={false}
    />
  );

  return (
    <div>
      <FormFieldTitle style={{ marginBottom: 0 }}>
        {t("labels.amount_per_trade")}
        <OverrideTypeToggle
          onClick={(overrideType) => {
            if (overrideType === OverrideType.USER_CONFIGURATION) {
              setAmountType(undefined);
            } else if (!amountType) {
              setAmountType(AmountType.PERCENTAGE);
            }
          }}
          options={[OverrideType.USER_CONFIGURATION, OverrideType.PERSONAL]}
          style={{ marginLeft: 8 }}
          selectedOption={
            amountType ? OverrideType.PERSONAL : OverrideType.USER_CONFIGURATION
          }
          showPersonalAsOn={true}
        />
      </FormFieldTitle>
      <ExpandCollapse
        expanded={!!amountType}
        overflowVisible={true}
        style={{ marginTop: 10 }}
      >
        <AmountWrapper $showError={!!invalidTradeAmount}>
          {amountValueInput}
          {amountTypeSelect}
        </AmountWrapper>
        {amountValidationMessage && (
          <Text
            $size={typography.fontSizes.S}
            type={invalidTradeAmount ? "danger" : "secondary"}
            style={{ marginTop: 6 }}
          >
            {amountValidationMessage}
          </Text>
        )}
      </ExpandCollapse>
    </div>
  );
};

export default SignalAmountSelector;
